import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

import {
  ADMIN_QUERY_KEYS,
  type AdNetworksResponse,
  type NetworkAccountsResponse,
} from "./adminTypes"

// ---------------------------------------------------------------------------
// Ad Networks
// ---------------------------------------------------------------------------

export function useAdNetworks() {
  return useQuery<AdNetworksResponse>({
    queryKey: ADMIN_QUERY_KEYS.adNetworks,
    queryFn: () => apiFetch<AdNetworksResponse>("/api/ad-networks"),
    retry: false,
  })
}

export function useCreateAdNetwork() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      slug: string
      base_url?: string
      api_key?: string
    }) =>
      apiFetch<{ success: boolean }>("/api/ad-networks", {
        method: "POST",
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adNetworks })
    },
  })
}

export function useUpdateAdNetwork() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      name?: string
      slug?: string
      base_url?: string
      api_key?: string
      is_enabled?: boolean
    }) =>
      apiFetch<{ success: boolean }>(`/api/ad-networks/${data.id}`, {
        method: "PUT",
        body: {
          name: data.name,
          slug: data.slug,
          base_url: data.base_url,
          api_key: data.api_key,
          is_enabled: data.is_enabled,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adNetworks })
    },
  })
}

export function useToggleAdNetworkEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; is_enabled: boolean }) =>
      apiFetch<{ success: boolean }>(`/api/ad-networks/${data.id}`, {
        method: "PUT",
        body: { is_enabled: data.is_enabled },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adNetworks })
    },
  })
}

export function useDeleteAdNetwork() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (networkId: number) =>
      apiFetch<{ success: boolean }>(`/api/ad-networks/${networkId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adNetworks })
    },
  })
}

export function useNetworkAccounts(slug: string | null) {
  return useQuery<NetworkAccountsResponse>({
    queryKey: ADMIN_QUERY_KEYS.networkAccounts(slug ?? ""),
    queryFn: () =>
      apiFetch<NetworkAccountsResponse>(`/api/ad-networks/${slug}/accounts`),
    enabled: slug !== null,
    retry: false,
  })
}

export function useUpdateNetworkAccounts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { slug: string; accountIds: string[] }) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/ad-networks/${data.slug}/accounts`,
        { method: "POST", body: data.accountIds },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}
